import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { ContextMenu } from "primereact/contextmenu"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  focusCellByContent,
  focusCellByDataId,
  pageToFirst,
  getPagingInfo,
  getFocusedRowIndex,
  unfocusAllCells } from "components/table/table-automation"
import { TextEdit } from "components/table/inputs"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
// Controls
import Controls from "components/tenant-market/increase/controls/"
// Data
import automationService from "data/automation"
import carouselService from "data/carousel"
import increaseService from "data/increase"
import keyService, * as chord from "data/key"
import propertyService from "data/property"
import { handleReferences } from "data/lib/handle-references"
import { copy, paste, canPaste } from "data/lib/copy-paste"
// Styles
import { TMIncreaseTable_s } from "components/tenant-market/increase/tm-increase-styles"

type ContextRow = {
  increaseid: number
  propertyid: number
  code: string
  systeminput?: string
}

const TMIncreaseTable = (props: {tableId: string, data: object[], defaults: object,  inflationData: object[]}) => {
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const traceMenuItem = {
    label: "Trace", icon: "pi pi-fw pi-search", command: e=> {
      const refreshFn = () => increaseService.fetchIncrease(contextRow.propertyid)
      handleReferences("increase", contextRow.increaseid, {refreshFn});
    }
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: "Duplicate Row", icon: "pi pi-fw pi-copy", command: e => {
      increaseService.copy(contextRow.propertyid, contextRow.increaseid, contextRow.code)
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        const increases = await increaseService.copies(contextRow.propertyid, contextRow.increaseid, n)
      })
    }},
    {label: "Delete Row", icon: "pi pi-fw pi-trash", command: e => {
      const currentRowNum = getFocusedRowIndex() + 1
      showDeleteConfirmation(contextRow, currentRowNum)
    }},
    traceMenuItem
  ]

  const systemMenu = [{...traceMenuItem}]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({increaseid: 0, propertyid: 0, code: ""})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field("increase")

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const increaseEventSubscription = increaseService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "INCREASE_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0)
          }, 500)
          break
        case e.type === "INCREASE_COPIED":
          setTimeout(() => {
            focusCellByContent(props.tableId, e.payload.code)
          }, 500)
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            increaseService.create(propertyService.getStoredPropertyId(), props.defaults)
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[(pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1];
            if (!currentRow.systeminput) showDeleteConfirmation({increaseid: currentRow.increaseid, propertyid: currentRow.propertyid, code: currentRow.code}, currentRowNum)
          }
          break
      }
    })

    return function cleanup() {
      carouselSubscription.unsubscribe()
      increaseEventSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, "current.filter", "", "contains");
    else if (searchFilter !== "") _.invoke(tableRef, "current.filter", searchFilter, "contains");
    else return
  }, [searchFilter])

  useEffect(() => {
    !contextRow.systeminput || (contextRow.systeminput && !contextRow.systeminput.startsWith("system")) ? setMenu(nonSystemMenu) : setMenu(systemMenu)
  }, [contextRow])

  const persist = (value, field, rowData) => {
    let increase = _.omit(_.cloneDeep(rowData), 'index')

    if (field === "increasedetail" && (value.inputtype === 'basic' || value.inputtype === '% of Inflation')){
      increase = value
      if (value.inputtype === '% of Inflation') _.set(increase, 'amttype', null)
    } else if (field === 'increasedetail'){
      _.set(increase, field, value)
      _.set(increase, 'inputtype', 'detailed')
      // Null out basic state values that are outside of increasedetail
      _.set(increase, 'amt', null);
      _.set(increase, 'amttype', null);
      _.set(increase, 'effectivemonth', null);
      _.set(increase, 'inflationdetail', null);
    } else {
      _.set(increase, field, value);
    }

    increaseService.patch(increase)
    if(value && field === "increasedetail") carouselService.updateRowData(increase)
  }

  useEffect(() => {
    if(canPaste(contextCell)) {
      nonSystemMenu.splice(1, 0, {label: 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => { if(contextRow.systeminput) return;
        paste(contextCell, contextRow, increaseService.fetchIncrease, 'increase')
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu(systemMenu) : setMenu(nonSystemMenu)
  }, [contextCell])

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    let pager = getPagingInfo(props.tableId)
    let row = currentRow + ((pager.currentPage - 1) * pager.pageSize);

    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        increaseService.deleteOne(record.propertyid, record.increaseid)
        setDeleteConfirmation(null)
        automationService.broadcast("MODAL_CLOSING", {})
        setTimeout(() => focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0), 200)
      },
      rowNum: row,
      code: record.code
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( props.tableId, contextRow.increaseid)}}
        copy={copy}
        show={true}
        title={contextRow.code}
      />
    )
  }

  function updateContextMenu(originalEvent){
    let systemRows = document.querySelectorAll(".system-row, .systemImmutable-row");
    for (let i of systemRows){
      if (originalEvent.target === i || i.contains(originalEvent.target)) {
        setMenu(systemMenu);
      } else {
        if (!menu.length){
          setMenu(nonSystemMenu)
        }
      }
    }
    _.invoke(menuRef.current, "show", originalEvent)
  }

  const rowClassName = (rowData) => {
    if (rowData.systeminput && rowData.systeminput === "system"){
      return {"system-row" : rowData.systeminput === "system"}
    }
    else if (rowData.systeminput && rowData.systeminput === "systemImmutable"){
      return {"systemImmutable-row" : rowData.systeminput === "systemImmutable"}
    }
    else return {"" : rowData}
  }

  const defaults:object = props.defaults;
  const inflationData:object[] = props.inflationData;

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      {defaults && inflationData.length > 0 && <TMIncreaseTable_s id={`${props.tableId}-scrollable-container`}>
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={props.data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth="45px"
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
        >
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column system-disabled"
            filter
            header="Code"
            {...field("code")}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column system-disabled"
            filter
            header="Name"
            {...field("name")}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "270px"}} className="p-editable-column"
            header="Input Type"
            {...field("increasedetail", (rowData, props) => { return (
              rowData.systeminput === "systemImmutable" ? <span style={{paddingLeft: 20}}>System</span> : <CarouselTrigger
                field="increasedetail"
                rowData={rowData}
                title="Input Type"
                persist={persist}
                defaults={defaults}
                controls={{
                  basic: {control: Controls.InputTypeBasic, name: "Basic"},
                  detailed: {control: Controls.InputTypeDetailed, name: "Detailed"},
                  '% of Inflation': {control: Controls.InputPercentOfInflation, name: "% of Inflation"}
                }}
                basicReadonly={['amt', {append: true}, 'amttype']}
                namedItems={{
                  inflation: inflationData
                }}
                componentAtRest={(props: {rowData: object, _field: string}) => {
                  let state = _.get(props.rowData, "inputtype")
                  let displayValue = ""

                  switch(true) {
                    case state === "basic":
                      displayValue = !rowData['amt'] || !rowData['amttype'] ? 'Basic' : `${rowData['amt'] || ''} ${rowData['amttype'] || ''}`
                      break
                    case state === "detailed":
                      displayValue = "Detailed"
                      break
                    case state === "% of Inflation":
                      displayValue = `${rowData['amt']} % of Inflation`
                      break
                  }
                  return <span>{displayValue}</span>
                }}
                hover={{
                  basic: [{key: 'amt', label: 'Amt', ignoreField: true},{key: 'amttype', label: 'Increase Type', ignoreField: true},{key: ['inflationdetail','inflationNamedItemId'], label: 'Inflation', ignoreField: true},{key: 'effectivemonth', label: 'Effective', ignoreField: true}],
                  detailed: {multiple: [{key: ['increasedetail', 'month'], label: 'Month'}, {key: ['increasedetail', 'increase'], label: 'Increase'},{key: 'increaseType', label: 'Increase Type'}]},
                  percentOfInflation: [{key: 'amt', label: 'Amt'},{key: ['inflationdetail','inflationNamedItemId'], label: 'Inflation'},{key: 'effectivemonth', label: 'Effective'}]
                }}
              />)},
              (err) => !_.includes(["code", "name"], err.path[0])
            )}
          />
        </DataTable>
        { carouselData && carouselData["show"] && <CarouselModal code="code" data={carouselData} key="carousel-modal" width={775} /> }
        { deleteConfirmation }
        { duplicateConfirmation }
        { props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
      </TMIncreaseTable_s>}
    </div>
  )
}

export default TMIncreaseTable
